import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Clock, Calendar, CheckCircle, Mail, Loader2, UserPlus, Send, X, Search, Download, UserCheck, Users, TrendingUp, Eye, DollarSign, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
type CustomerType = 'lead' | 'buyer';
type CustomerStatus = 'active' | 'invited' | 'inactive';
type Customer = {
  id: string;
  type: CustomerType;
  status: CustomerStatus;
  email?: string;
  lastLogin: string;
  age: string;
  timeSpent: string;
  model: string;
  projectName: string;
  purchaseDate?: string;
  contractValue?: number;
  upgradeValue: number;
};
const customers: Customer[] = [{
  id: 'User-8823',
  type: 'buyer',
  status: 'active',
  email: 'john.doe@example.com',
  lastLogin: '10 mins ago',
  age: '3 days',
  timeSpent: '2h 15m',
  model: 'The Lawson',
  projectName: 'Oakwood Estates',
  purchaseDate: '2024-01-15',
  contractValue: 650000,
  upgradeValue: 35800
}, {
  id: 'User-9941',
  type: 'lead',
  status: 'invited',
  lastLogin: 'Never',
  age: '1 day',
  timeSpent: '0m',
  model: '-',
  projectName: 'Oakwood Estates',
  upgradeValue: 0
}, {
  id: 'User-7732',
  type: 'lead',
  status: 'active',
  lastLogin: '1 hour ago',
  age: '5 days',
  timeSpent: '4h 30m',
  model: 'The Lawson',
  projectName: 'Oakwood Estates',
  upgradeValue: 24500
}, {
  id: 'User-6621',
  type: 'lead',
  status: 'inactive',
  lastLogin: '5 days ago',
  age: '2 weeks',
  timeSpent: '45m',
  model: 'The Samson',
  projectName: 'Maple Residences',
  upgradeValue: 53500
}, {
  id: 'User-5519',
  type: 'buyer',
  status: 'active',
  email: 'jane.smith@example.com',
  lastLogin: 'Just now',
  age: '1 week',
  timeSpent: '1h 20m',
  model: 'The Lawson',
  projectName: 'Oakwood Estates',
  purchaseDate: '2024-01-18',
  contractValue: 680000,
  upgradeValue: 17200
}, {
  id: 'User-4408',
  type: 'lead',
  status: 'active',
  lastLogin: '30 mins ago',
  age: '2 days',
  timeSpent: '3h 45m',
  model: 'The Samson',
  projectName: 'Maple Residences',
  upgradeValue: 126800
}, {
  id: 'User-3397',
  type: 'buyer',
  status: 'active',
  email: 'mike.johnson@example.com',
  lastLogin: '2 hours ago',
  age: '1 month',
  timeSpent: '5h 10m',
  model: 'The Lawson',
  projectName: 'Oakwood Estates',
  purchaseDate: '2024-01-10',
  contractValue: 625000,
  upgradeValue: 13500
}];
const projects = [{
  id: 1,
  name: 'The Lawson'
}, {
  id: 2,
  name: 'The Samson'
}];
export function BuyersPage() {
  const navigate = useNavigate();
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<'form' | 'sending' | 'success'>('form');
  const [inviteData, setInviteData] = useState({
    email: '',
    projectId: ''
  });
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'leads' | 'buyers'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | CustomerStatus>('all');
  // Sorting
  const [sortField, setSortField] = useState<keyof Customer | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Filtered and sorted customers
  const filteredCustomers = useMemo(() => {
    let filtered = customers;
    // Type filter
    if (typeFilter === 'leads') {
      filtered = filtered.filter(c => c.type === 'lead');
    } else if (typeFilter === 'buyers') {
      filtered = filtered.filter(c => c.type === 'buyer');
    }
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => c.id.toLowerCase().includes(query) || c.model.toLowerCase().includes(query) || c.projectName.toLowerCase().includes(query) || c.email?.toLowerCase().includes(query));
    }
    // Sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        // Handle undefined/null values
        if (aVal === undefined || aVal === null) aVal = '';
        if (bVal === undefined || bVal === null) bVal = '';
        // Numeric comparison for upgradeValue
        if (sortField === 'upgradeValue') {
          return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
        }
        // String comparison for other fields
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (sortDirection === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }
    return filtered;
  }, [typeFilter, statusFilter, searchQuery, sortField, sortDirection]);
  // Stats
  const stats = useMemo(() => {
    const totalLeads = customers.filter(c => c.type === 'lead').length;
    const totalBuyers = customers.filter(c => c.type === 'buyer').length;
    const activeLeads = customers.filter(c => c.type === 'lead' && c.status === 'active').length;
    const conversionRate = totalLeads > 0 ? (totalBuyers / (totalLeads + totalBuyers) * 100).toFixed(1) : '0';
    const totalUpgradeValue = customers.reduce((sum, c) => sum + c.upgradeValue, 0);
    const avgUpgradeValue = customers.length > 0 ? Math.round(totalUpgradeValue / customers.filter(c => c.upgradeValue > 0).length) : 0;
    return {
      totalLeads,
      totalBuyers,
      activeLeads,
      conversionRate,
      total: customers.length,
      totalUpgradeValue,
      avgUpgradeValue
    };
  }, []);
  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showInviteModal) {
          handleCloseInviteModal();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showInviteModal]);
  const handleInviteBuyer = () => {
    setInviteStatus('sending');
    setTimeout(() => {
      setInviteStatus('success');
    }, 1500);
  };
  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteStatus('form');
    setInviteData({
      email: '',
      projectId: ''
    });
  };
  const handleRowClick = (customer: Customer) => {
    navigate(`/${builderId}/customers/${customer.id}`);
  };
  const handleSort = (field: keyof Customer) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending (highest first)
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const SortIcon = ({
    field
  }: {
    field: keyof Customer;
  }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-slate-400" data-id="element-1281" />;
    }
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4 text-primary-600" data-id="element-1282" /> : <ArrowDown className="h-4 w-4 text-primary-600" data-id="element-1283" />;
  };
  return <>
      <div className="space-y-6 max-w-7xl mx-auto" data-id="element-1284">
        <div className="flex items-center justify-between" data-id="element-1285">
          <div data-id="element-1286">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-1287">Customers</h1>
            <p className="text-slate-500" data-id="element-1288">
              Manage leads and buyers, track engagement and upgrade selections.
            </p>
          </div>
          <Button onClick={() => setShowInviteModal(true)} leftIcon={<UserPlus className="h-4 w-4" data-id="element-1290" />} data-id="element-1289">
            Invite Customer
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-1291">
          <Card noPadding data-id="element-1292">
            <div className="p-4" data-id="element-1293">
              <div className="flex items-center justify-between mb-2" data-id="element-1294">
                <p className="text-sm text-slate-500" data-id="element-1295">Total Customers</p>
                <Users className="h-4 w-4 text-slate-400" data-id="element-1296" />
              </div>
              <p className="text-2xl font-bold text-slate-900" data-id="element-1297">{stats.total}</p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1298">
                {stats.totalLeads} leads, {stats.totalBuyers} buyers
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-1299">
            <div className="p-4" data-id="element-1300">
              <div className="flex items-center justify-between mb-2" data-id="element-1301">
                <p className="text-sm text-slate-500" data-id="element-1302">Active Leads</p>
                <Eye className="h-4 w-4 text-blue-500" data-id="element-1303" />
              </div>
              <p className="text-2xl font-bold text-blue-600" data-id="element-1304">
                {stats.activeLeads}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1305">Currently exploring</p>
            </div>
          </Card>

          <Card noPadding data-id="element-1306">
            <div className="p-4" data-id="element-1307">
              <div className="flex items-center justify-between mb-2" data-id="element-1308">
                <p className="text-sm text-slate-500" data-id="element-1309">Total Buyers</p>
                <UserCheck className="h-4 w-4 text-green-500" data-id="element-1310" />
              </div>
              <p className="text-2xl font-bold text-green-600" data-id="element-1311">
                {stats.totalBuyers}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1312">Confirmed purchases</p>
            </div>
          </Card>

          <Card noPadding data-id="element-1313">
            <div className="p-4" data-id="element-1314">
              <div className="flex items-center justify-between mb-2" data-id="element-1315">
                <p className="text-sm text-slate-500" data-id="element-1316">Total Upgrades</p>
                <DollarSign className="h-4 w-4 text-emerald-500" data-id="element-1317" />
              </div>
              <p className="text-2xl font-bold text-emerald-600" data-id="element-1318">
                ${stats.totalUpgradeValue.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1319">All selections</p>
            </div>
          </Card>

          <Card noPadding data-id="element-1320">
            <div className="p-4" data-id="element-1321">
              <div className="flex items-center justify-between mb-2" data-id="element-1322">
                <p className="text-sm text-slate-500" data-id="element-1323">Avg Upgrade Value</p>
                <TrendingUp className="h-4 w-4 text-purple-500" data-id="element-1324" />
              </div>
              <p className="text-2xl font-bold text-purple-600" data-id="element-1325">
                ${stats.avgUpgradeValue.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1326">Per customer</p>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card noPadding data-id="element-1327">
          <div className="p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl" data-id="element-1328">
            <div className="flex items-center gap-3" data-id="element-1329">
              {/* Search */}
              <div className="flex-1 relative" data-id="element-1330">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-1331" />
                <input type="text" placeholder="Search by ID, email, model, or project..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1332" />
              </div>

              {/* Type Filter */}
              <div className="flex border border-slate-300 rounded-md overflow-hidden bg-white" data-id="element-1333">
                <button onClick={() => setTypeFilter('all')} className={`px-4 py-2 text-sm font-medium transition-colors ${typeFilter === 'all' ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1334">
                  All ({stats.total})
                </button>
                <button onClick={() => setTypeFilter('leads')} className={`px-4 py-2 text-sm font-medium border-l border-slate-300 transition-colors ${typeFilter === 'leads' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1335">
                  Leads ({stats.totalLeads})
                </button>
                <button onClick={() => setTypeFilter('buyers')} className={`px-4 py-2 text-sm font-medium border-l border-slate-300 transition-colors ${typeFilter === 'buyers' ? 'bg-green-50 text-green-700' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1336">
                  Buyers ({stats.totalBuyers})
                </button>
              </div>

              {/* Status Filter */}
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1337">
                <option value="all" data-id="element-1338">All Status</option>
                <option value="active" data-id="element-1339">Active</option>
                <option value="invited" data-id="element-1340">Invited</option>
                <option value="inactive" data-id="element-1341">Inactive</option>
              </select>

              <Button variant="outline" size="sm" leftIcon={<Download className="h-4 w-4" data-id="element-1343" />} data-id="element-1342">
                Export
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-1344">
            <table className="w-full text-left text-sm" data-id="element-1345">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-1346">
                <tr data-id="element-1347">
                  <th className="px-6 py-4 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort('id')} data-id="element-1348">
                    <div className="flex items-center gap-2" data-id="element-1349">
                      Customer ID
                      <SortIcon field="id" data-id="element-1350" />
                    </div>
                  </th>
                  <th className="px-6 py-4 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort('type')} data-id="element-1351">
                    <div className="flex items-center gap-2" data-id="element-1352">
                      Type
                      <SortIcon field="type" data-id="element-1353" />
                    </div>
                  </th>
                  <th className="px-6 py-4 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort('status')} data-id="element-1354">
                    <div className="flex items-center gap-2" data-id="element-1355">
                      Status
                      <SortIcon field="status" data-id="element-1356" />
                    </div>
                  </th>
                  <th className="px-6 py-4 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort('model')} data-id="element-1357">
                    <div className="flex items-center gap-2" data-id="element-1358">
                      Model
                      <SortIcon field="model" data-id="element-1359" />
                    </div>
                  </th>
                  <th className="px-6 py-4 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort('upgradeValue')} data-id="element-1360">
                    <div className="flex items-center gap-2" data-id="element-1361">
                      Upgrade Value
                      <SortIcon field="upgradeValue" data-id="element-1362" />
                    </div>
                  </th>
                  <th className="px-6 py-4 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort('timeSpent')} data-id="element-1363">
                    <div className="flex items-center gap-2" data-id="element-1364">
                      Engagement
                      <SortIcon field="timeSpent" data-id="element-1365" />
                    </div>
                  </th>
                  <th className="px-6 py-4 cursor-pointer hover:text-slate-700 transition-colors" onClick={() => handleSort('lastLogin')} data-id="element-1366">
                    <div className="flex items-center gap-2" data-id="element-1367">
                      Last Active
                      <SortIcon field="lastLogin" data-id="element-1368" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1369">
                {filteredCustomers.length === 0 ? <tr data-id="element-1370">
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500" data-id="element-1371">
                      No customers found matching your filters
                    </td>
                  </tr> : filteredCustomers.map(customer => <tr key={customer.id} onClick={() => handleRowClick(customer)} className="hover:bg-slate-50 transition-colors cursor-pointer group" data-id="element-1372">
                      <td className="px-6 py-4" data-id="element-1373">
                        <div className="flex items-center gap-2" data-id="element-1374">
                          <span className="font-medium text-slate-900" data-id="element-1375">
                            {customer.id}
                          </span>
                          <ChevronRight className="h-4 w-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1376" />
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1377">
                        <Badge variant={customer.type === 'buyer' ? 'success' : 'primary'} data-id="element-1378">
                          {customer.type === 'buyer' ? <div className="flex items-center gap-1" data-id="element-1379">
                              <UserCheck className="h-3 w-3" data-id="element-1380" />
                              Buyer
                            </div> : <div className="flex items-center gap-1" data-id="element-1381">
                              <Eye className="h-3 w-3" data-id="element-1382" />
                              Lead
                            </div>}
                        </Badge>
                      </td>
                      <td className="px-6 py-4" data-id="element-1383">
                        <Badge variant={customer.status === 'active' ? 'success' : customer.status === 'invited' ? 'warning' : 'neutral'} data-id="element-1384">
                          {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-1385">
                        {customer.model}
                      </td>
                      <td className="px-6 py-4" data-id="element-1386">
                        {customer.upgradeValue > 0 ? <span className="font-semibold text-emerald-600" data-id="element-1387">
                            ${customer.upgradeValue.toLocaleString()}
                          </span> : <span className="text-slate-400" data-id="element-1388">-</span>}
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-1389">
                        <div className="flex items-center gap-1" data-id="element-1390">
                          <Clock className="h-3 w-3 text-slate-400" data-id="element-1391" />
                          {customer.timeSpent}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-1392">
                        <div className="flex items-center gap-1" data-id="element-1393">
                          <Calendar className="h-3 w-3 text-slate-400" data-id="element-1394" />
                          {customer.lastLogin}
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Invite Customer Modal */}
      {showInviteModal && <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1395">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-1396">
            {inviteStatus === 'form' ? <>
                <div className="p-6 border-b border-slate-200 flex items-center justify-between" data-id="element-1397">
                  <div data-id="element-1398">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-1399">
                      Invite Customer
                    </h2>
                    <p className="text-sm text-slate-500 mt-1" data-id="element-1400">
                      Send an invitation to explore your project
                    </p>
                  </div>
                  <button onClick={handleCloseInviteModal} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1401">
                    <X className="h-5 w-5" data-id="element-1402" />
                  </button>
                </div>
                <div className="p-6 space-y-4" data-id="element-1403">
                  <div data-id="element-1404">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1405">
                      Customer Email
                    </label>
                    <input type="email" value={inviteData.email} onChange={e => setInviteData({
                ...inviteData,
                email: e.target.value
              })} placeholder="customer@example.com" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-1406" />
                  </div>
                  <div data-id="element-1407">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1408">
                      Project
                    </label>
                    <select value={inviteData.projectId} onChange={e => setInviteData({
                ...inviteData,
                projectId: e.target.value
              })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1409">
                      <option value="" data-id="element-1410">Select a project...</option>
                      {projects.map(project => <option key={project.id} value={project.id} data-id="element-1411">
                          {project.name}
                        </option>)}
                    </select>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2" data-id="element-1412">
                    <Mail className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-1413" />
                    <p className="text-xs text-blue-800" data-id="element-1414">
                      The customer's email will be kept private and not visible
                      to your team.
                    </p>
                  </div>
                </div>
                <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-1415">
                  <Button variant="outline" onClick={handleCloseInviteModal} data-id="element-1416">
                    Cancel
                  </Button>
                  <Button onClick={handleInviteBuyer} disabled={!inviteData.email || !inviteData.projectId} leftIcon={<Send className="h-4 w-4" data-id="element-1418" />} data-id="element-1417">
                    Send Invitation
                  </Button>
                </div>
              </> : inviteStatus === 'sending' ? <>
                <div className="p-6 text-center" data-id="element-1419">
                  <div className="mx-auto w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4" data-id="element-1420">
                    <Loader2 className="h-8 w-8 text-primary-600 animate-spin" data-id="element-1421" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1422">
                    Sending Invitation
                  </h2>
                  <p className="text-slate-500 text-sm" data-id="element-1423">
                    Sending invitation to {inviteData.email}...
                  </p>
                </div>
              </> : <>
                <div className="p-6 border-b border-slate-200 flex items-center justify-between" data-id="element-1424">
                  <h2 className="text-xl font-bold text-slate-900" data-id="element-1425">
                    Invitation Sent Successfully
                  </h2>
                  <button onClick={handleCloseInviteModal} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1426">
                    <X className="h-5 w-5" data-id="element-1427" />
                  </button>
                </div>
                <div className="p-6" data-id="element-1428">
                  <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4" data-id="element-1429">
                    <CheckCircle className="h-8 w-8 text-green-600" data-id="element-1430" />
                  </div>
                  <p className="text-slate-500 text-sm mb-6 text-center" data-id="element-1431">
                    An invitation has been sent to {inviteData.email}
                  </p>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm" data-id="element-1432">
                    <div className="flex justify-between" data-id="element-1433">
                      <span className="text-slate-600" data-id="element-1434">Email:</span>
                      <span className="font-medium text-slate-900" data-id="element-1435">
                        {inviteData.email}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1436">
                      <span className="text-slate-600" data-id="element-1437">Project:</span>
                      <span className="font-medium text-slate-900" data-id="element-1438">
                        {projects.find(p => p.id === Number(inviteData.projectId))?.name}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1439">
                      <span className="text-slate-600" data-id="element-1440">Status:</span>
                      <Badge variant="warning" data-id="element-1441">Invited</Badge>
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-1442">
                  <Button onClick={handleCloseInviteModal} data-id="element-1443">Done</Button>
                </div>
              </>}
          </div>
        </div>}
    </>;
}